import Hue from '@assets/hue_hero.svg';
import GalleryTabButton from '@atoms/GalleryTabButton/GalleryTabButton';
import Image from '@atoms/Image/Image';
import ShadowContainerHorizontal from '@atoms/ShadowContainerHorizontal/ShadowContainerHorizontal';
import IExternalVideo from '@models/IExternalVideo';
import { IImage } from '@models/IImage';
import { IMeta } from '@models/IMeta';
import Carousel from '@molecules/Carousel/Carousel';
import EmbeddedVideo from '@molecules/EmbeddedVideo/EmbeddedVideo';
import PaginationArrows from '@molecules/PaginationArrows/PaginationArrows';
import updateDataLayer from '@utils/helpers/gtm';
import replaceWithNonBreakingHyphens from '@utils/helpers/replaceWithNonBreakingHyphens';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import useResizeObserver from '@utils/hooks/useResizeObserver';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { useRef, useState } from 'react';
import { SwiperSlide } from 'swiper/react';
import scrollIntoViewWithOffset from '../../../utility/helpers/scrollIntoViewWithOffset';

interface Props {
  readonly meta?: IMeta;
  readonly images?: IImage[];
  readonly video?: IExternalVideo;
  readonly backgroundImage?: IImage;
  readonly videoLabel?: string;
  readonly imageLabel?: string;
  readonly videoIcon?: string;
  readonly imageIcon?: string;
  readonly anchorId?: string;
  readonly title?: string;
  readonly subtitle?: string;
  readonly dividerText?: string;
}

const enum GalleryTabsEnum {
  Video = 'video',
  Images = 'images',
}

const ProductImageGallery = ({
  meta,
  images,
  video,
  backgroundImage,
  videoLabel = '',
  imageLabel = '',
  videoIcon = '',
  imageIcon = '',
  anchorId = '',
  title,
  subtitle,
  dividerText = '/',
}: Props) => {
  const isTablet = useMediaQuery(1024);
  const isMobile = useMediaQuery(768);
  const [paginationRef, { width: paginationWidth }] = useResizeObserver();
  const containerRef = useRef<HTMLDivElement>(null);

  // When 3D is implemented, tab must be called 'view_3d' to integrate with Nikon GA
  const [activeTab, setActiveTab] = useState(
    images?.length ? GalleryTabsEnum.Images : GalleryTabsEnum.Video
  );
  const [currentImageSlideIndex, setCurrentImageSlideIndex] = useState(1);
  const carouselNavId = 'product-image-gallery';
  const imagesPaginationCounterText = `${currentImageSlideIndex} ${dividerText} ${
    images?.length ?? 0
  }`;
  const showTabs = video && !!images?.length;

  const setTab = (tab: GalleryTabsEnum) => {
    setActiveTab(tab);
    updateDataLayer({
      event: 'selectContent',
      content_type: 'product_image_side_menu',
      selection: tab,
    });
  };

  if (!video && !images?.length) return null;

  return (
    <section
      id={`a-${anchorId}`}
      className="bg-black-100 pt-20 pb-20 text-white xl:pt-28"
      data-epi-block-id={meta?.contentLink}
      data-testid="CT-14-Product-Image-Gallery"
    >
      <div
        className={classNames('container', {
          'flex flex-col xl:flex-row': title && subtitle,
        })}
        ref={containerRef}
      >
        {title && subtitle && (
          <div className="flex flex-col justify-center gap-6 text-center max-xl:items-center max-xl:px-2.5 max-xl:pb-10 xl:w-2/5 xl:gap-10 xl:pr-15 xl:text-left 2xl:pr-24">
            <h1
              className="mount"
              data-testid="CT-14-Product-Image-Gallery-Title"
            >
              {replaceWithNonBreakingHyphens(title)}
            </h1>
            <p
              className={classNames('text-grey-400', isTablet ? 'h5' : 'h3')}
              data-testid="CT-14-Product-Image-Gallery-Subtitle"
            >
              {subtitle}
            </p>
          </div>
        )}

        <div
          className={classNames('relative z-[1] flex w-full flex-col', {
            'xl:w-3/5': title && subtitle,
          })}
        >
          {activeTab === GalleryTabsEnum.Video && (
            <motion.div
              className="w-full pr-0 xl:pr-[150px]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              exit={{ opacity: 0 }}
            >
              <EmbeddedVideo {...video} embedded />
            </motion.div>
          )}

          {activeTab === GalleryTabsEnum.Images && (
            <motion.div
              className="w-full"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              exit={{ opacity: 0 }}
            >
              <div className="relative select-none">
                <Carousel
                  tabIndex={0}
                  slidesPerView={1}
                  loop
                  navId={carouselNavId}
                  onSlideChange={(swiper) => {
                    setCurrentImageSlideIndex(swiper.realIndex + 1);
                  }}
                  id={carouselNavId}
                  data-testid="CT-14-Product-Image-Gallery-Carousel"
                >
                  {images?.map((image, index) => (
                    <SwiperSlide
                      key={`Product-Gallery-${image.desktop?.url}`}
                      className="stretch-card flex items-center justify-center"
                    >
                      <div className="flex h-[350px] w-full select-none md:h-[500px]">
                        <Image
                          image={image}
                          classes="flex justify-center w-full"
                          imageClasses="object-contain w-full h-full"
                          lazyLoad={index !== 0}
                          dataTestId={`CT-14-Product-Image-Gallery-Image-${index}`}
                        />
                      </div>
                    </SwiperSlide>
                  ))}

                  {backgroundImage && (
                    <Image
                      image={backgroundImage}
                      classes="w-full"
                      imageClasses="absolute left-1/2 -translate-x-1/2 xl:flex xl:px-[200px] h-full w-auto inset-y-0 z-0"
                      dataTestId="CT-14-Product-Image-Gallery-Background-Image"
                    />
                  )}
                </Carousel>

                <div className="-z-[1] pointer-events-none absolute top-0 left-0 flex h-full w-full items-center justify-center">
                  <img
                    alt="background-hue"
                    src={`${Hue}`}
                    className="w-full object-contain lg:w-3/4"
                  />
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </div>

      <AnimatePresence>
        {images && images?.length > 1 && (
          <motion.div
            className={classNames(
              'mt-13 h-10 items-center justify-center',
              activeTab !== GalleryTabsEnum.Images ? 'hidden' : 'flex',
              {
                'lg:hidden': !!video,
              }
            )}
            initial={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            animate={{
              opacity: 1,
            }}
          >
            <PaginationArrows
              carouselNavId={carouselNavId}
              componentName="Product Image Gallery"
              counterText={imagesPaginationCounterText}
              arrowIconSize={isTablet ? '0.7rem' : '1.05rem'}
              productName={title}
              dataTestId="CT-14-Product-Image-Gallery-Pagination"
            />
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showTabs && (
          <motion.div
            initial={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            animate={{
              opacity:
                paginationWidth > 0 || images?.length === 1 || isTablet ? 1 : 0,
            }}
          >
            <ShadowContainerHorizontal className="mt-10 lg:mt-28">
              <div
                className="flex items-center justify-around gap-x-5 md:justify-center"
                data-testid="CT-14-Product-Image-Gallery-Tab-Buttons"
              >
                {video && (
                  <div className="pointer-events-auto">
                    <GalleryTabButton
                      onClick={() => {
                        setCurrentImageSlideIndex(1);
                        setTab(GalleryTabsEnum.Video);
                        if (isMobile) {
                          scrollIntoViewWithOffset(
                            containerRef.current as HTMLElement,
                            120
                          );
                        }
                      }}
                      label={videoLabel}
                      icon={videoIcon}
                      isActive={activeTab === GalleryTabsEnum.Video}
                    />
                  </div>
                )}

                {images && (
                  <div className="pointer-events-auto relative">
                    <GalleryTabButton
                      onClick={() => {
                        setTab(GalleryTabsEnum.Images);
                        if (isMobile) {
                          scrollIntoViewWithOffset(
                            containerRef.current as HTMLElement,
                            120
                          );
                        }
                      }}
                      label={imageLabel}
                      icon={imageIcon}
                      isActive={activeTab === GalleryTabsEnum.Images}
                      rightDesktopPadding={
                        images?.length > 1 &&
                        activeTab === GalleryTabsEnum.Images
                          ? paginationWidth + 45
                          : 15
                      }
                    />

                    {images?.length > 1 &&
                      activeTab === GalleryTabsEnum.Images && (
                        <div
                          ref={paginationRef}
                          className="absolute top-0 right-[15px] flex h-full items-center max-lg:hidden"
                        >
                          <PaginationArrows
                            carouselNavId={carouselNavId}
                            componentName="Product Image Gallery"
                            counterText={imagesPaginationCounterText}
                            isCounterTextBetweenArrows
                            productName={title}
                            hasWrapper={false}
                          />
                        </div>
                      )}
                  </div>
                )}
              </div>
            </ShadowContainerHorizontal>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default ProductImageGallery;
